import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer den QUALIFIKATIONEN-Tab der Seite "Organisation
 * & Struktur" (`organization-overview`). Gestartet ueber den tab-bewussten
 * `<app-dialog-help-button>` in `organization-header` (Pattern analog
 * `EMPLOYEE_LIST_HELP_TOUR`).
 *
 * Kernbotschaft: Qualifikationen sind nicht nur Etiketten — sie steuern das
 * Mitarbeiter↔Schicht-Matching (der Auto-Vorschlag plant nur passend
 * Qualifizierte ein).
 *
 * `onBeforeShow` am Welcome-Step sichert defensiv den Qualifikationen-Tab.
 * Cue-Daten (Tabellen-Spalten) im Manifest unter `org-skills-help`.
 */
function ensureSkillsTab(): Promise<void> {
  const tab = document.querySelector<HTMLElement>('[onboardingAnchor="skills-tab"]');
  if (tab && !tab.classList.contains('is-active')) {
    tab.click();
    return new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );
  }
  return Promise.resolve();
}

export const ORG_SKILLS_HELP_TOUR: TourConfig = {
  id: TourFeature.OrgSkillsHelp,
  version: 1,
  title: 'ORG_SKILLS_HELP_TOUR_TITLE',
  description: 'ORG_SKILLS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: {
    route: '/organization',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 95,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'ORG_SKILLS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.OrganizationView,
          spotlightAnchor: AnchorId.OrganizationView,
          title: 'TUTORIAL.ORG_SKILLS.WELCOME.TITLE',
          content: 'TUTORIAL.ORG_SKILLS.WELCOME.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
          onBeforeShow: ensureSkillsTab,
        },
        {
          id: 'add',
          anchor: AnchorId.AddSkillButton,
          spotlightAnchor: AnchorId.AddSkillButton,
          title: 'TUTORIAL.ORG_SKILLS.ADD.TITLE',
          content: 'TUTORIAL.ORG_SKILLS.ADD.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="add-skill-button"]'),
        },
        {
          id: 'table',
          // Cues pulsen die Spaltenkoepfe (Manifest): Symbol, Abteilungen, Team.
          anchor: AnchorId.SkillsTable,
          spotlightAnchor: AnchorId.SkillsTable,
          title: 'TUTORIAL.ORG_SKILLS.TABLE.TITLE',
          content: 'TUTORIAL.ORG_SKILLS.TABLE.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="skills-table"]'),
        },
        {
          id: 'matching',
          anchor: AnchorId.SkillsTable,
          spotlightAnchor: AnchorId.SkillsTable,
          title: 'TUTORIAL.ORG_SKILLS.MATCHING.TITLE',
          content: 'TUTORIAL.ORG_SKILLS.MATCHING.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="skills-table"]'),
        },
        {
          id: 'row-actions',
          anchor: AnchorId.SkillsColActions,
          spotlightAnchor: AnchorId.SkillsColActions,
          title: 'TUTORIAL.ORG_SKILLS.ROW_ACTIONS.TITLE',
          content: 'TUTORIAL.ORG_SKILLS.ROW_ACTIONS.CONTENT',
          placement: 'left',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="skills-col-actions"]'),
        },
      ],
    },
  ],
};
